import { Company, Office, UseRowsReturn } from '@nettbureau/featured-core';
import { groupBy, merge, sample } from 'lodash-es';
import { Marker } from './components/map/GoogleMap';

/** Determines if card-containers should render .*/
function getShouldRenderRow<T>(rows: UseRowsReturn<T>) {
  if (rows.rows.length === 0) {
    return false;
  }

  if (!rows.rows[0]) {
    return false;
  }

  return rows.rows[0].items.length > 0;
}

/** Build person rows. */
function buildPersonRows(companies: (Office | Company)[]) {
  const people = companies
    .filter((company) => company.subscriptionLevel === 'premium')
    .flatMap((company) =>
      company.people.map((person) =>
        merge(person, {
          attributes: {
            companyName: company.name,
            logo: company.logo,
          },
        }),
      ),
    );

  const peopleGroupedById = groupBy(people, 'id');

  return Object.keys(peopleGroupedById)
    .map((personId) => {
      const person = peopleGroupedById[personId];

      if (!person) {
        return null;
      }

      if (person.length > 1) {
        return sample(person);
      }

      return person[0];
    })
    .filter((person) => person !== null);
}

/** Build product rows */
function buildProductRows(companies: (Office | Company)[]) {
  return companies
    .filter((company) => company.subscriptionLevel === 'premium')
    .flatMap((company) => company.products.map((product) => product));
}

/** Build trust factor rows */
function buildTrustFactorRows(companies: (Office | Company)[]) {
  return companies
    .filter((company) => company.subscriptionLevel === 'premium')
    .flatMap((company) => company.trustFactors.map((product) => product));
}

/** Builds props for Google Maps component. */
function buildGoogleMapsProps(companies: (Office | Company)[]): Marker[] {
  return companies
    .map((attributes) => {
      return attributes.location !== null
        ? {
            id: attributes.id,
            lat: +(attributes.location.latitude ?? 0),
            lng: +(attributes.location.longitude ?? 0),
            name: attributes.name,
            color: attributes.subscriptionLevel === 'premium' ? 'red' : 'gray',
            url: attributes.internalLink,
            postarea: attributes.postarea,
            postcode: attributes.postcode,
            address: attributes.address,
          }
        : null;
    })
    .filter((item): item is Marker => item !== null);
}

export {
  buildGoogleMapsProps,
  buildPersonRows,
  buildProductRows,
  buildTrustFactorRows,
  getShouldRenderRow,
};
